import { useEffect, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { updateCase, type CaseRecordData } from '@/lib/cases';
import { Badge, Button, Textarea } from '@/components/ui';
import { LaudoBlocoRecolhivel } from '@/components/LaudoBlocoRecolhivel';

const t = ptBR.informacoesRelevantes;

interface InformacoesRelevantesPanelProps {
  caseRecord: CaseRecordData;
  onSaved: (atualizado: CaseRecordData) => void;
}

/**
 * Campo autoral do perito no laudo (E58.F3 — rodada 3, item 11). // ux-neutrality:allow doc-comment
 *
 * Registro de achado que NÃO entra na comparação formal: o exemplo que motivou // ux-neutrality:allow doc-comment
 * o pedido foi a geolocalização de uma assinatura digital coincidindo com a de // ux-neutrality:allow doc-comment
 * um contrato contestado. O texto sai em seção própria do documento, rotulada // ux-neutrality:allow doc-comment
 * como fora da comparação — e o rótulo é o que impede o achado de parecer parte // ux-neutrality:allow doc-comment
 * da régua calibrada. // ux-neutrality:allow doc-comment
 */
export function InformacoesRelevantesPanel({
  caseRecord,
  onSaved,
}: InformacoesRelevantesPanelProps) {
  const client = useApiClient();
  const persistido = caseRecord.informacoes_relevantes ?? '';
  const [texto, setTexto] = useState(persistido);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(false);
  const [salvo, setSalvo] = useState(false);
  // E65.F1.T02: achado autoral é registro ocasional — abre para escrever e
  // recolhe de volta quando o texto foi gravado.
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    setTexto(persistido);
  }, [persistido]);

  const alterado = texto !== persistido;

  async function salvar(): Promise<void> {
    setSalvando(true);
    setErro(false);
    setSalvo(false);
    try {
      // Texto em branco limpa o campo: o perito que apaga o achado quer que ele
      // saia do laudo, não que vire uma seção vazia.
      const atualizado = await updateCase(client, caseRecord.id, {
        informacoes_relevantes: texto.trim() === '' ? null : texto,
      });
      onSaved(atualizado);
      setSalvo(true);
      setAberto(false);
    } catch {
      setErro(true);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <LaudoBlocoRecolhivel
      testId="informacoes-relevantes-panel"
      kicker={t.kicker}
      titulo={t.titulo}
      aberto={aberto}
      onAbertoChange={setAberto}
      // Com o bloco fechado, a marca de preenchido é o que impede o achado já
      // registrado de parecer inexistente.
      acessorio={
        persistido.trim() === '' ? undefined : (
          <Badge variant="neutral" data-testid="informacoes-relevantes-preenchido">
            {ptBR.laudoCaso.blocoRecolhivel.preenchido}
          </Badge>
        )
      }
      // Confirmação e erro sobrevivem ao recolhimento: o bloco fecha assim que
      // grava, e sem isso o salvamento ficaria sem desfecho visível.
      rodape={
        salvo || erro ? (
          <>
            {salvo && !alterado && (
              <span
                role="status"
                className="text-body-sm text-fg-muted"
                data-testid="informacoes-relevantes-salvo"
              >
                {t.salvoConfirmacao}
              </span>
            )}
            {erro && (
              <p role="alert" className="bg-danger-50 px-4 py-3 text-body-sm text-danger">
                {t.erroSalvar}
              </p>
            )}
          </>
        ) : undefined
      }
    >
      <p className="max-w-3xl text-body-sm text-fg-muted">{t.descricao}</p>

      <label className="mt-sc-4 block">
        <span className="text-caption font-medium text-fg-muted">{t.campoLabel}</span>
        <Textarea
          className="mt-1 min-h-32"
          data-testid="informacoes-relevantes-campo"
          value={texto}
          maxLength={8000}
          placeholder={t.placeholder}
          onChange={(evento) => {
            setTexto(evento.target.value);
            setSalvo(false);
          }}
        />
      </label>

      <div className="mt-sc-3 flex flex-wrap items-center gap-sc-3">
        <Button
          type="button"
          data-testid="informacoes-relevantes-salvar"
          disabled={!alterado || salvando}
          onClick={() => void salvar()}
        >
          {salvando ? t.salvando : t.salvar}
        </Button>
      </div>

      <p className="mt-sc-3 max-w-3xl text-caption text-fg-muted">{t.nota}</p>
    </LaudoBlocoRecolhivel>
  );
}
